import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedRichText, LocalizedText } from "@/i18n/LocalizedText";
import { useFormContext } from "react-hook-form";
import type { FieldError } from "react-hook-form";
import type { MouseEvent } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { auth, remoteConfig } from "../firebase";
import type { UseFormReturn, FieldValues } from "react-hook-form";
import { Spinner } from "@/components/ui/spinner";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { updateProfile, type User } from "firebase/auth";
import { useState } from "react";
import { Check, Eye, EyeOff, RotateCcw } from "lucide-react";
import { Input } from "./ui/input";
import { useQuery } from "@tanstack/react-query";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { requireEmailVerification } from "@/config/auth";
import { toast } from "sonner";
import { authAPI } from "@/services/api";
import { AUTH_FLOW_DEFAULTS, parseAuthFlowConfig } from "@/lib/authFlowConfig";
import { useAuth } from "./AuthContext";
import { rememberTryReturn } from "@/lib/tryLink";

interface Step1<T extends FieldValues> {
  setLoadingStep: (value: boolean) => void;
  methods: UseFormReturn<T>;
  error: string;
  loadingStep: boolean;
  handleGoogleSignIn: () => void;
  isGoogleSignInBlockedInAppBrowser?: boolean;
  onOpenGoogleSignInBrowserHelp?: () => void;
  setError: (error: string) => void;
  prefilledEmail?: string;
}

export default function Step1<T extends FieldValues>({
  handleGoogleSignIn,
  error,
  loadingStep,
  methods,
  setError,
  setLoadingStep,
  isGoogleSignInBlockedInAppBrowser = false,
  onOpenGoogleSignInBrowserHelp,
  prefilledEmail,
}: Step1<T>) {
  const { t } = useLocale();
  const {
    register,
    formState: { errors },
    getValues,
  } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);
  const [showConformPassword, setShowConformPassword] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const navigate = useNavigate();
  const { refreshUser, setEmailVerifiedState } = useAuth();
  const location = useLocation();
  const verifyQuery = new URLSearchParams(location.search).get("verify");
  const currentUser = auth.currentUser;
  const isPendingVerificationMode = Boolean(
    requireEmailVerification && currentUser && !currentUser.emailVerified,
  );
  const showInvalidVerificationMessage = verifyQuery === "invalid";

  const getApiErrorMessage = (error: unknown, fallback: string) => {
    const err = error as {
      response?: { data?: { message?: string } };
      message?: string;
    };
    const responseMessage = String(err?.response?.data?.message || "").trim();
    if (responseMessage) return responseMessage;
    const message = String(err?.message || "").trim();
    return message || fallback;
  };

  const waitForBackendOnboardingSync = async () => {
    const delay = (ms: number) =>
      new Promise<void>((resolve) => window.setTimeout(resolve, ms));
    for (let attempt = 0; attempt < 6; attempt += 1) {
      try {
        const me = await authAPI.getMe();
        const onboardStep = Number(me?.user?.onboard_step || 0);
        if (onboardStep >= 1) {
          return me;
        }
      } catch {
        // Keep retrying until backend session/user state is visible.
      }
      await delay(250 * (attempt + 1));
    }
    return null;
  };

  const sendVerificationEmail = async () => {
    try {
      const response = await authAPI.sendVerificationEmail();
      if (response?.skipped && response?.reason === "already_verified") {
        navigate("/register/2");
        return;
      }
      toast.success(t("legacy.verification.email.sent.98kyfm"));
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          "Could not send verification email now. Try again in a few minutes.",
        ),
      );
    }
  };

  const handleResendVerification = async () => {
    if (!auth.currentUser) {
      setError("Please sign in to resend verification email.");
      return;
    }
    setResendingVerification(true);
    try {
      await sendVerificationEmail();
    } finally {
      setResendingVerification(false);
    }
  };

  const handleContinueAfterVerification = async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      setError("Please sign in to continue.");
      return;
    }
    setCheckingVerification(true);
    try {
      await firebaseUser.reload();
      if (firebaseUser.emailVerified) {
        setEmailVerifiedState(true);
        const idToken = await firebaseUser.getIdToken(true);
        if (idToken) {
          await authAPI.login({ idToken });
        }
        const syncedMe = await waitForBackendOnboardingSync();
        await refreshUser().catch(() => undefined);
        setError("");
        const nextStep = Math.max(
          2,
          Math.min(4, Number(syncedMe?.user?.onboard_step || 1) + 1),
        );
        navigate(`/register/${nextStep}`, { replace: true });
        return;
      }
      setError(
        "Email is not verified yet. Please check your inbox and try again.",
      );
    } catch (error) {
      setError(
        getApiErrorMessage(
          error,
          "Couldn't continue right now. Please try again.",
        ),
      );
    } finally {
      setCheckingVerification(false);
    }
  };

  const handleSwitchToSignIn = async (event: MouseEvent<HTMLAnchorElement>) => {
    if (new URLSearchParams(location.search).get("returnTo") === "/try") {
      rememberTryReturn("login");
    }
    if (!isPendingVerificationMode) return;
    event.preventDefault();
    setLoadingStep(true);
    try {
      await Promise.allSettled([authAPI.logout(), signOut(auth)]);
      navigate("/login", { replace: true });
    } finally {
      setLoadingStep(false);
    }
  };

  const { data: authConfig, isLoading } = useQuery({
    queryKey: ["remoteConfig", "authFlow"],
    queryFn: async () => {
      try {
        await fetchAndActivate(remoteConfig);
        const rawValue = getValue(remoteConfig, "authFlow").asString();
        return parseAuthFlowConfig(rawValue);
      } catch {
        return { ...AUTH_FLOW_DEFAULTS };
      }
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const registerUser = async () => {
    const isValid = await methods.trigger();
    if (isValid) {
      setLoadingStep(true);
      // TO/DO - phone add
      // const { email, password, fullname, phone } = getValues();
      const { email, password, fullname } = getValues();
      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );
        const user: User = userCredential.user;
        await updateProfile(user, {
          displayName: fullname,
        });
        if (requireEmailVerification) {
          await sendVerificationEmail();
          setError("");
          navigate("/register/1?verify=pending");
        } else {
          setError("");
          navigate("/register/2");
        }
      } catch (err: unknown) {
        const code = String((err as any)?.code || "").toLowerCase();
        const message = String((err as any)?.message || "").toLowerCase();
        const isEmailExists =
          code === "auth/email-already-in-use" ||
          message.includes("email_exists") ||
          message.includes("email-already-in-use") ||
          message.includes("email already");

        if (isEmailExists) {
          if (requireEmailVerification) {
            try {
              const existingCredential = await signInWithEmailAndPassword(
                auth,
                String(email),
                String(password),
              );
              const existingUser = existingCredential.user;
              if (!existingUser.emailVerified) {
                await sendVerificationEmail();
                setError("");
                navigate("/register/1?verify=pending");
                return;
              }
              setError("This email is already registered. Please sign in.");
              setLoadingStep(false);
              return;
            } catch (signInErr: any) {
              const signInCode = String(signInErr?.code || "").toLowerCase();
              if (
                signInCode === "auth/invalid-credential" ||
                signInCode === "auth/wrong-password"
              ) {
                setError(
                  "This email is already registered. Password doesn't match. Sign in or reset password.",
                );
              } else {
                setError(
                  "This email is already registered. Sign in and use Resend email.",
                );
              }
              setLoadingStep(false);
              return;
            }
          }
          setError("This email is already registered. Please sign in.");
          setLoadingStep(false);
          return;
        }

        if (code === "auth/invalid-email") {
          setError("Please enter a valid email address.");
        } else if (code === "auth/weak-password") {
          setError("Password is too weak. Use at least 6 characters.");
        } else if (code === "auth/too-many-requests") {
          setError("Too many attempts. Please try again later.");
        } else {
          const raw = String((err as any)?.message || "Failed to sign up");
          const cleaned = raw
            .replace(/^Firebase:\s*/i, "")
            .replace(/\s*\(auth\/[^\)]+\)\.?/i, "")
            .trim();
          setError(cleaned || "Failed to sign up");
        }
        setLoadingStep(false);
      }
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2
          className={
            isPendingVerificationMode
              ? "text-2xl font-semibold text-center mb-2"
              : "text-xl font-semibold text-center mb-6"
          }
        >
          {isPendingVerificationMode
            ? t("legacy.verify.your.email.1v02yhb")
            : t("legacy.sign.up.1hce6r7")}
        </h2>
        {isPendingVerificationMode ? (
          <div className="w-full max-w-md mx-auto">
            <p className="text-center text-sm text-[#737373] mb-6 leading-6">
              <LocalizedRichText
                id="registration.verifyEmail.sent"
                values={{
                  email: (
                    <span className="font-semibold">
                      {currentUser?.email || t("legacy.your.email.11y1394")}
                    </span>
                  ),
                }}
              />
            </p>
            {showInvalidVerificationMessage ? (
              <p className="text-center text-sm text-[#B42318] mb-4">
                <LocalizedText id="legacy.your.verification.link.is.invalid.or.expired.request.1jtkv" />
              </p>
            ) : null}
            <p className="text-center text-sm text-muted-foreground mb-6">
              <LocalizedText id="legacy.click.the.link.in.your.inbox.to.continue.1bnryoy" />
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                type="button"
                disabled={resendingVerification}
                className="grow"
                onClick={handleResendVerification}
              >
                {resendingVerification ? (
                  <div className="mr-2 flex justify-center items-center">
                    <Spinner variant="circle-filled" size={16} />
                  </div>
                ) : (
                  <RotateCcw className="mr-2" />
                )}
                <LocalizedText id="legacy.resend.1igpwcu" />
              </Button>
              <Button
                type="button"
                className="hover:bg-[#ff8138] grow cursor-pointer bg-[#FF5E00] text-white"
                disabled={checkingVerification}
                onClick={handleContinueAfterVerification}
              >
                {checkingVerification ? (
                  <div className="mr-2 flex justify-center items-center">
                    <Spinner variant="circle-filled" size={16} />
                  </div>
                ) : (
                  <Check className="ml-2" />
                )}
                <LocalizedText id="legacy.i.and.apos.ve.verified.11bj3vt" />
              </Button>
            </div>
            {error ? (
              <p className="text-red-500 text-sm mt-4 text-center">{error}</p>
            ) : null}
          </div>
        ) : (
          <>
            {showInvalidVerificationMessage ? (
              <div className="mb-6 rounded-md border border-[#F4D2BF] bg-[#FFF7F2] px-4 py-3 text-sm text-[#7A3A1D]">
                <LocalizedText id="legacy.this.verification.link.is.invalid.or.expired.sign.1agze3h" />
              </div>
            ) : null}
            {isGoogleSignInBlockedInAppBrowser ? (
              <div className="mb-6 rounded-md border border-[#F4D2BF] bg-[#FFF7F2] px-4 py-3 text-sm text-[#7A3A1D]">
                <p>
                  <LocalizedText id="legacy.google.sign.in.is.unavailable.in.this.in.t08833" />
                </p>
                <button
                  type="button"
                  onClick={() => onOpenGoogleSignInBrowserHelp?.()}
                  className="mt-2 cursor-pointer text-sm font-medium underline underline-offset-2"
                >
                  <LocalizedText id="legacy.how.to.open.in.browser.1v1fegh" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="cursor-pointer mb-6 w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-md flex items-center justify-center hover:bg-gray-100 transition"
              >
                <img
                  src="https://www.svgrepo.com/show/475656/google-color.svg"
                  alt={t("legacy.google.16fzjn0")}
                  className="w-5 h-5 mr-2"
                />
                <LocalizedText id="legacy.sign.in.with.google.1bo26yi" />
              </button>
            )}
          </>
        )}
      </div>

      {!isPendingVerificationMode &&
        (authConfig?.isEmailPassRegisterShow ?? true) &&
        !isLoading && (
          <>
            <div className="text-center mb-6 relative">
              {" "}
              <Separator />
              <span className="capitalize font-sans text-[#737373] text-xs absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-2 bg-white">
                <LocalizedText id="legacy.or.continue.with.hj9huj" />
              </span>
            </div>

            <div>
              <Label
                className="mb-3 text-sm font-medium text-[#0A0A0A]"
                htmlFor="email"
              >
                <LocalizedText id="legacy.email.12rd9wz" />
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="off"
                {...register("email")}
                disabled={Boolean(prefilledEmail)}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300 ${(errors.email as FieldError)?.message && "border-red-500 focus-visible:border-red-500 focus-visible:ring-red-200 focus:ring-red-200"}`}
              />
              {prefilledEmail && (
                <p className="text-xs text-muted-foreground mt-2">
                  <LocalizedRichText
                    id="registration.invite.forEmail"
                    values={{ email: prefilledEmail }}
                  />
                </p>
              )}
              {errors.email && (
                <p className="text-red-500 text-sm mt-2">
                  {(errors.email as FieldError)?.message}
                </p>
              )}
            </div>
            {/* <div>
            <Label
              className="mb-1 text-md font-normal text-gray-700"
              htmlFor="phone"
            >
              Phone:
            </Label>
            <Input
              type="tel"
              id="phone"
              autoComplete="off"
              {...register("phone")}
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300 ${(errors.phone as FieldError)?.message && "border-red-500 focus-visible:border-red-500 focus-visible:ring-red-200 focus:ring-red-200"}`}
            />
            {errors.phone && (
              <p className="text-red-500 text-sm mt-2">
                {(errors.phone as FieldError)?.message}
              </p>
            )}
          </div> */}

            <div>
              <Label
                className="mb-3 text-sm font-medium text-[#0A0A0A]"
                htmlFor="fullname"
              >
                <LocalizedText id="legacy.full.name.j5g8gb" />
              </Label>
              <Input
                id="fullname"
                type="text"
                autoComplete="off"
                {...register("fullname")}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300 ${(errors.email as FieldError)?.message && "border-red-500 focus-visible:border-red-500 focus-visible:ring-red-200 focus:ring-red-200"}`}
              />
              {errors.fullname && (
                <p className="text-red-500 text-sm mt-2">
                  {(errors.fullname as FieldError)?.message}
                </p>
              )}
            </div>

            <div className="relative">
              <Label
                className="mb-3 text-sm font-medium text-[#0A0A0A]"
                htmlFor="password"
              >
                <LocalizedText id="legacy.password.10amvls" />
              </Label>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="off"
                {...register("password")}
                className={`w-full pl-4 pr-10 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300 ${(errors.email as FieldError)?.message && "border-red-500 focus-visible:border-red-500 focus-visible:ring-red-200 focus:ring-red-200"}`}
              />
              <button
                className="absolute cursor-pointer text-muted-foreground right-2 top-[39px] "
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {!showPassword ? (
                  <EyeOff size={20} strokeWidth={1.5} />
                ) : (
                  <Eye size={20} strokeWidth={1.5} />
                )}
              </button>
              {errors.password && (
                <p className="text-red-500 text-sm mt-2">
                  {(errors.password as FieldError)?.message}
                </p>
              )}
            </div>

            <div className="relative">
              <Label
                className="mb-3 text-sm font-medium text-[#0A0A0A]"
                htmlFor="confirmPassword"
              >
                <LocalizedText id="legacy.confirm.password.1p1ksr6" />
              </Label>
              <Input
                id="confirmPassword"
                type={showConformPassword ? "text" : "password"}
                autoComplete="off"
                {...register("confirmPassword")}
                className={`w-full pl-4 pr-10 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300 ${(errors.email as FieldError)?.message && "border-red-500 focus-visible:border-red-500 focus-visible:ring-red-200 focus:ring-red-200"}`}
              />
              <button
                className="absolute text-muted-foreground cursor-pointer right-2 top-[39px] "
                type="button"
                onClick={() => setShowConformPassword((prev) => !prev)}
              >
                {!showConformPassword ? (
                  <EyeOff size={20} strokeWidth={1.5} />
                ) : (
                  <Eye size={20} strokeWidth={1.5} />
                )}
              </button>
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-2">
                  {(errors.confirmPassword as FieldError)?.message}
                </p>
              )}
            </div>

            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            <div className="flex items-center justify-center">
              <Button
                disabled={loadingStep}
                className={`flex py-[22px] px-[45px] cursor-pointer hover:bg-[#ff8138] hover:text-white text-white bg-[#FF5E00]`}
                onClick={() => registerUser()}
              >
                {loadingStep ? (
                  <div className=" mr-2 flex justify-center items-center">
                    <Spinner variant="circle-filled" size={16}></Spinner>
                  </div>
                ) : null}
                <LocalizedText id="legacy.continue.5iqttw" />
              </Button>
            </div>
          </>
        )}

      <div className="mt-2 text-center text-sm ">
        <LocalizedText id="legacy.already.have.an.account.o8gsso" />
        <Link
          to={`/login${location.search}`}
          replace
          onClick={handleSwitchToSignIn}
          className="ml-1 text-black underline underline-offset-1"
        >
          <LocalizedText id="legacy.sign.in.1hce7g1" />
        </Link>
      </div>
    </div>
  );
}
